import { useState, useEffect, useCallback } from 'react';
import { 
  onSnapshot, 
  query, 
  collection, 
  orderBy, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Générer un numéro de référence unique
const generateReference = () => {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `PLT-${year}${month}-${random}`;
};

const toSafeDate = (value) => {
  if (!value) return new Date();

  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    if (converted instanceof Date && !Number.isNaN(converted.getTime())) {
      return converted;
    }
    return new Date();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

// Hook pour l'admin (toutes les plaintes)
export const usePlainte = () => {
  return usePlaintes();
};

export const usePlaintes = () => {
  const [plaintes, setPlaintes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    nouvelles: 0,
    enCours: 0,
    traitees: 0,
    classees: 0
  });

  useEffect(() => {
    const q = query(collection(db, 'plaintes'), orderBy('date', 'desc'));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(doc => {
          const plainteData = doc.data();
          return {
            id: doc.id,
            ...plainteData,
            date: toSafeDate(plainteData.date)
          };
        });
        
        setPlaintes(data);
        
        // Calculer les stats
        setStats({
          total: data.length,
          nouvelles: data.filter(p => p.statut === 'nouvelle').length,
          enCours: data.filter(p => p.statut === 'en_cours').length,
          traitees: data.filter(p => p.statut === 'traitee').length,
          classees: data.filter(p => p.statut === 'classee').length
        });
        
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Erreur Réclamations:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Créer une nouvelle plainte
  const createPlainte = async (data) => {
    try {
      const reference = generateReference();
      console.log('Nouvelle Réclamation créée avec référence:', reference);
      
      const plainteData = {
        ...data,
        reference,
        statut: 'nouvelle',
        date: serverTimestamp(),
        historique: [{
          date: new Date().toISOString(),
          statut: 'nouvelle',
          commentaire: 'Réclamation déposée'
        }]
      };
      
      const docRef = await addDoc(collection(db, 'plaintes'), plainteData);
      console.log('Réclamation sauvegardée avec ID:', docRef.id);
      
      return { 
        success: true, 
        id: docRef.id, 
        reference,
        message: 'Votre Réclamation a été déposée avec succès. Conservez votre numéro de référence.'
      };
    } catch (err) {
      console.error('Erreur création Réclamation:', err);
      return { success: false, error: err.message };
    }
  };

  // Mettre à jour le statut d'une plainte
  const updateStatut = async (id, nouveauStatut, commentaire = '') => {
    try {
      const plainteRef = doc(db, 'plaintes', id);
      const plainte = plaintes.find(p => p.id === id);
      
      const nouvelHistorique = [
        ...(plainte.historique || []),
        {
          date: new Date().toISOString(),
          statut: nouveauStatut,
          commentaire: commentaire || `Statut mis ? jour : ${nouveauStatut}`
        }
      ];
      
      await updateDoc(plainteRef, {
        statut: nouveauStatut,
        historique: nouvelHistorique,
        dateMiseAJour: serverTimestamp()
      });
      
      return { success: true };
    } catch (err) {
      console.error('Erreur mise à jour statut:', err);
      return { success: false, error: err.message };
    }
  };

  // Ajouter une réponse à une plainte
  const addReponse = async (id, reponse) => {
    try {
      const plainteRef = doc(db, 'plaintes', id);
      const plainte = plaintes.find(p => p.id === id);
      
      await updateDoc(plainteRef, {
        reponse: reponse,
        dateReponse: serverTimestamp(),
        historique: [
          ...(plainte.historique || []),
          {
            date: new Date().toISOString(),
            statut: plainte.statut,
            commentaire: 'Réponse ajoutée par la Médiature'
          }
        ]
      });
      
      return { success: true };
    } catch (err) {
      console.error('Erreur ajout réponse:', err);
      return { success: false, error: err.message };
    }
  };

  // Supprimer une plainte
  const deletePlainte = async (id) => {
    try {
      await deleteDoc(doc(db, 'plaintes', id));
      return { success: true };
    } catch (err) {
      console.error('Erreur suppression Réclamation:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    plaintes,
    loading,
    error,
    stats,
    createPlainte,
    updateStatut,
    addReponse,
    deletePlainte
  };
};

// Hook pour le suivi d'une plainte spécifique (par référence)
export const usePlainteSuivi = (reference) => {
  const [plainte, setPlainte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reference) {
      setLoading(false);
      setPlainte(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Requête simple sans where pour éviter les problèmes d'index
    const unsubscribe = onSnapshot(
      query(collection(db, 'plaintes'), orderBy('date', 'desc')),
      (snapshot) => {
        const plaintes = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            date: toSafeDate(data.date)
          };
        });
        
        console.log('Total Réclamations chargées:', plaintes.length);
        console.log('Références disponibles:', plaintes.map(p => p.reference));
        
        // Chercher la plainte avec la référence (insensible à la casse)
        const searchRef = reference.toUpperCase().trim();
        console.log('Recherche de:', searchRef);
        
        const found = plaintes.find(p => {
          const match = p.reference && p.reference.toUpperCase() === searchRef;
          if (p.reference) {
            console.log(`Comparaison: "${p.reference.toUpperCase()}" === "${searchRef}" -> ${match}`);
          }
          return match;
        });
        
        console.log('Réclamation trouvée:', found);
        
        if (found) {
          setPlainte(found);
          setError(null);
        } else {
          setPlainte(null);
          setError('Aucune Réclamation trouvée avec cette référence');
        }
        
        setLoading(false);
      },
      (err) => {
        console.error('Erreur recherche Réclamation:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [reference]);

  return { plainte, loading, error };
};

// Statuts possibles avec labels et couleurs
export const STATUTS_PLAINTE = {
  nouvelle: { label: 'Nouvelle', color: '#3B82F6', bg: '#DBEAFE' },
  en_cours: { label: 'En cours', color: '#F59E0B', bg: '#FEF3C7' },
  traitee: { label: 'Traitée', color: '#10B981', bg: '#D1FAE5' },
  classee: { label: 'Classée', color: '#6B7280', bg: '#F3F4F6' }
};

// Types de plaintes
export const TYPES_PLAINTE = [
  { value: 'conflit_foncier', label: 'Conflit foncier' },
  { value: 'conflit_agricole', label: 'Conflit agricole' },
  { value: 'conflit_pastoral', label: 'Conflit pastoral' },
  { value: 'harcelement', label: 'Harcèlement' },
  { value: 'discrimination', label: 'Discrimination' },
  { value: 'abus_pouvoir', label: 'Abus de pouvoir' },
  { value: 'corruption', label: 'Corruption' },
  { value: 'autre', label: 'Autre' }
];

// Localités (préférences communes au Tchad)
export const LOCALITES = [
  'N\'Djaména',
  'Moundou',
  'Sarh',
  'Abéché',
  'Kélo',
  'Koumra',
  'Pala',
  'Am Timan',
  'Mongo',
  'Ati',
  'Bongor',
  'Doba',
  'Lai',
  'Faya-Largeau',
  'Guelendeng',
  'Kyabé',
  'Massakory',
  'Moussoro',
  'Oum Hadjer',
  'Biltine'
];
